import { Fragment } from 'react';
import { FiShield, FiRefreshCw, FiAlertTriangle, FiEdit2, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import { IoIosRefresh } from 'react-icons/io';
import ThreeColumnLayout from '../../layouts/ThreeColumnLayout';
import FilterSidebar from '../../organisms/FilterSidebar';
import FilterSkeleton from '../../atoms/FilterSkeleton';
import ATable from '../../organisms/ATable';
import TableSkeleton from '../../atoms/TableSkeleton';
import Loading from '../../ions/Loading';
import TextEdit from '../../molecules/TextEdit';
import PageFooter from '../../molecules/PageFooter';
import NewTooltip from '../../atoms/NewTooltip';
import PismoStatusBadge from './shared/PismoStatusBadge';
import CreditRightFilterSidebar from './shared/CreditRightFilterSidebar';
import CreditRightItemView from './shared/CreditRightItemView';
import ReceivableItemView from './ReceivableItemView';
import { formatCurrency, formatDate, formatCpfCnpj } from '../../../utils/collateralFormatters';
import useCreditRightPageList from '../../../hooks/useCreditRightPageList';

const ERROR_STATUSES = ['REJECTED', 'FAILED_RETRYABLE', 'FAILED_PERMANENT'];

const columns = [
    { id: 'internalNumber', title: 'Nº interno', field: 'internalNumber', canSort: true, width: '150px' },
    { id: 'debtor', title: 'Sacado', field: 'debtorName', canSort: true },
    { id: 'nfe', title: 'NFe / Título', field: 'nfe', canSort: false, width: '150px' },
    { id: 'faceValue', title: 'Valor', field: 'faceValue', canSort: true, cssClass: 'text-right', width: '120px' },
    { id: 'maturity', title: 'Vencimento', field: 'maturityDate', canSort: true, width: '100px' },
    { id: 'registeredAt', title: 'Registrado em', field: 'registeredAt', canSort: true, width: '110px' },
    { id: 'status', title: 'Status', field: 'status', canSort: true, cssClass: 'text-center', width: '150px' },
];

interface OperationGroup {
    groupKey: string;
    creditOperationId: string;
    operationCode: string;
    assignorName: string;
    items: any[];
}

// Group the flat list by credit operation, preserving row order.
const groupByOperation = (rows: any[]): OperationGroup[] => {
    const groups: OperationGroup[] = [];
    const index: Record<string, OperationGroup> = {};
    rows.forEach((row) => {
        // Group by the real operation id. When it is missing (should not happen —
        // credit_operation_id is NOT NULL — but guard anyway), fall back to a
        // per-row unique key so unrelated records are never merged into one group.
        const key = row.creditOperationId || `no-op:${row.id}`;
        if (!index[key]) {
            index[key] = {
                groupKey: key,
                creditOperationId: row.creditOperationId || '',
                operationCode: row.operationCode || '—',
                assignorName: row.assignorName || '',
                items: [],
            };
            groups.push(index[key]);
        }
        index[key].items.push(row);
    });
    return groups;
};

const CreditRightPageList = () => {
    const {
        rows, total, page, setPage, pageSize,
        isLoading, initialLoading,
        apiInfo, statusCounts,
        statusFilter, setStatusFilter,
        searchInput, setSearchInput, setSearch,
        sortField, setSortField, sortOrder, setSortOrder,
        selectedItem, setSelectedItem,
        selectedIds, toggleSelect, toggleSelectAll, toggleSelectGroup, resendableIds,
        resending, resendSelected,
        collapsedOps, toggleCollapse,
        receivableItem, openReceivable, closeReceivable, reloadReceivable,
        refresh, handleRowClick,
    } = useCreditRightPageList();

    const groups = groupByOperation(rows);
    // ATable only injects the select-all column when canSelectAll is true. Keep the
    // body's checkbox <td> and the group/empty colSpans in sync with the same flag,
    // otherwise header and rows misalign when nothing is resendable.
    const hasSelectColumn = resendableIds.length > 0;
    const totalColSpan = columns.length + (hasSelectColumn ? 1 : 0);

    const filterGroups = initialLoading ? [] : [
        {
            title: '',
            children: (
                <CreditRightFilterSidebar
                    statusFilter={statusFilter}
                    onStatusChange={setStatusFilter}
                    statusCounts={statusCounts}
                />
            ),
        },
    ];

    return (
        <>
            {isLoading && <Loading />}

            <ThreeColumnLayout
                leftSidebarTitle={
                    <div className="flex items-center justify-between w-full">
                        <div className="flex items-center space-x-2">
                            <FiShield className="text-blue-600" size={18} />
                            <span>Garantias</span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                            {apiInfo.version && <div>v{apiInfo.version}</div>}
                            <div className={`font-medium ${apiInfo.environment === 'prd' ? 'text-red-600' :
                                apiInfo.environment === 'hml' ? 'text-yellow-600' : 'text-green-600'}`}>
                                {(apiInfo.environment || '').toUpperCase()}
                            </div>
                        </div>
                    </div>
                }
                leftSidebar={
                    <FilterSidebar filterGroups={filterGroups}>
                        {initialLoading && <FilterSkeleton />}
                    </FilterSidebar>
                }
                showRightPanel={false}
                onCloseRightPanel={() => { }}
                rightPanel={null}
            >
                <div className="flex flex-col h-full bg-white dark:bg-slate-800 relative overflow-hidden">
                    <div className="flex-shrink-0 border-b border-gray-200 dark:border-slate-700 p-3 bg-white dark:bg-slate-800 z-10">
                        <div className="flex items-center justify-between">
                            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Registros Pismo</h1>
                            <div className="flex items-center space-x-2">
                                <button
                                    onClick={resendSelected}
                                    disabled={resending || selectedIds.size === 0}
                                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                    title="Reenviar registros selecionados">
                                    <FiRefreshCw size={14} />
                                    {resending ? 'Reenviando...' : `Reenviar selecionados${selectedIds.size ? ` (${selectedIds.size})` : ''}`}
                                </button>
                                <button onClick={refresh}
                                    className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                    title="Atualizar lista">
                                    <IoIosRefresh size={18} />
                                </button>
                                <div className="w-72">
                                    <TextEdit label="Buscar sacado, NFe, nº interno..." value={searchInput}
                                        onChange={e => setSearchInput(e.target.value)}
                                        onKeyDown={(e: any) => { if (e.key === 'Enter') { setSearch(searchInput); setPage(1); } }} />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex-1 overflow-auto [&_table]:table-fixed [&_table]:w-full">
                        {initialLoading ? (
                            <TableSkeleton rows={12} />
                        ) : (
                            <ATable headers={columns} fieldSorted={sortField} orderSorted={sortOrder}
                                onSort={(f: string, o: 'ASC' | 'DESC') => { setSortField(f); setSortOrder(o); }}
                                canSelectAll={hasSelectColumn}
                                onSelect={(checked: boolean) => toggleSelectAll(checked)}>
                                {rows.length === 0 ? (
                                    <tr><td colSpan={totalColSpan} className="py-8 text-center text-gray-500 dark:text-gray-400">Nenhum registro encontrado</td></tr>
                                ) : groups.map((group) => {
                                    const groupResendableIds = group.items.filter((i: any) => i.canResend).map((i: any) => Number(i.id));
                                    const groupResendable = groupResendableIds.length > 0;
                                    const selectedInGroup = groupResendableIds.filter((id: number) => selectedIds.has(id)).length;
                                    const allSelected = groupResendable && selectedInGroup === groupResendableIds.length;
                                    const someSelected = selectedInGroup > 0 && !allSelected;
                                    const collapsed = collapsedOps.has(group.groupKey);
                                    return (
                                    <Fragment key={group.groupKey}>
                                        <tr className="bg-gray-100 dark:bg-slate-900/80">
                                            <td colSpan={totalColSpan} className="py-1.5 px-3 border-b border-gray-200 dark:border-slate-700 text-xs font-semibold text-gray-700 dark:text-gray-200">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-2">
                                                        {hasSelectColumn && (
                                                        <input
                                                            type="checkbox"
                                                            checked={allSelected}
                                                            ref={(el) => { if (el) el.indeterminate = someSelected; }}
                                                            disabled={!groupResendable}
                                                            onChange={(e) => toggleSelectGroup(groupResendableIds, e.target.checked)}
                                                            onClick={(e) => e.stopPropagation()}
                                                            title={groupResendable ? 'Selecionar todos reenviáveis desta operação' : 'Nenhum registro reenviável nesta operação'}
                                                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded-sm focus:ring-blue-500 disabled:opacity-30 disabled:cursor-not-allowed" />
                                                        )}
                                                        <button
                                                            onClick={() => toggleCollapse(group.groupKey)}
                                                            className="flex items-center gap-1.5 hover:text-blue-600 dark:hover:text-blue-400"
                                                            title={collapsed ? 'Expandir operação' : 'Colapsar operação'}>
                                                            {collapsed ? <FiChevronRight size={13} /> : <FiChevronDown size={13} />}
                                                            <span>
                                                                {group.operationCode}
                                                                {group.assignorName && <span className="ml-2 font-normal text-gray-500 dark:text-gray-400">— {group.assignorName}</span>}
                                                                <span className="ml-2 font-normal text-gray-400">({group.items.length})</span>
                                                            </span>
                                                        </button>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                        {!collapsed && group.items.map((row: any, index: number) => (
                                            <tr key={row.id}
                                                className={`group hover:bg-blue-50 dark:hover:bg-slate-700 cursor-pointer ${index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-900'}`}
                                                onClick={() => handleRowClick(row)}>
                                                {hasSelectColumn && (
                                                <td className="py-2 px-1 border-b border-gray-200 dark:border-slate-700 text-center" onClick={e => e.stopPropagation()}>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedIds.has(Number(row.id))}
                                                        disabled={!row.canResend}
                                                        onChange={() => toggleSelect(Number(row.id))}
                                                        title={row.canResend ? 'Selecionar para reenvio' : 'Registro não reenviável neste status'}
                                                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded-sm focus:ring-blue-500 disabled:opacity-30 disabled:cursor-not-allowed" />
                                                </td>
                                                )}
                                                <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-white font-mono text-[11px] truncate" title={row.internalNumber}>{row.internalNumber || '—'}</td>
                                                <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-white truncate" title={row.debtorName}>
                                                    <div>{row.debtorName || '—'}</div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400">{formatCpfCnpj(row.debtorDocument)}</div>
                                                </td>
                                                <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-xs dark:text-white truncate" title={row.nfe}>
                                                    <div className="font-mono truncate">{row.nfe || '—'}</div>
                                                    <div className="text-gray-500 dark:text-gray-400">Título {row.documentTitle || '—'} / Parc. {row.installmentNumber ?? '—'}</div>
                                                </td>
                                                <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-right text-sm dark:text-white">{formatCurrency(row.faceValue)}</td>
                                                <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-white">{row.maturityDate ? formatDate(row.maturityDate) : '—'}</td>
                                                <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-white">{row.registeredAt ? formatDate(row.registeredAt) : '—'}</td>
                                                <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center">
                                                    <div className="flex items-center justify-center gap-1">
                                                        <PismoStatusBadge status={row.status} statusFormatted={row.statusFormatted} />
                                                        {ERROR_STATUSES.includes(row.status) && (
                                                            <NewTooltip content={<span className="block w-64 whitespace-normal break-words text-left">{row.errorMessage || row.errorCodeFormatted || row.errorCode || 'Erro no registro'}</span>} alignment="left">
                                                                <FiAlertTriangle className="text-red-500 flex-shrink-0" size={14} />
                                                            </NewTooltip>
                                                        )}
                                                        {row.cityUnresolved && (
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); openReceivable(row.receivableId); }}
                                                                title="Editar recebível para corrigir a cidade"
                                                                className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-blue-500 hover:text-blue-700">
                                                                <FiEdit2 size={14} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </Fragment>
                                    );
                                })}
                            </ATable>
                        )}
                    </div>

                    <div className="flex-shrink-0 border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                        <PageFooter currentPage={page - 1} totalPages={Math.ceil(total / pageSize)} itemsPerPage={pageSize} totalElements={total} onPageChange={(p) => setPage(p + 1)} />
                    </div>
                </div>
            </ThreeColumnLayout>

            {selectedItem && (
                <CreditRightItemView item={selectedItem} onClose={() => setSelectedItem(null)} />
            )}

            {receivableItem && (
                <ReceivableItemView
                    item={receivableItem}
                    onClose={closeReceivable}
                    onCityResolved={() => { reloadReceivable(); refresh(); }}
                    onUpdated={() => { closeReceivable(); refresh(); }}
                />
            )}
        </>
    );
};

export default CreditRightPageList;
